import React, { useState } from "react";
import { CloudUpload, DeleteOutline, DriveFileRenameOutline, Edit, FileDownload } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";

import { useFeatureFlag } from "fond/featureFlags";
import { getCurrentProject } from "fond/project";
import { useAppSelector } from "fond/utils/hooks";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { ConfirmModal, StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

export type LayerPanelAction = "draw" | "upload" | "export" | "delete" | "path-preferences";

interface LayerPanelProps {
  title: string;
  layerKey: string;
  testIdPrefix?: string;
  actions?: LayerPanelAction[];
  drawTitle?: string;
  drawSubtitle?: string;
  onDraw?: () => void;
  onDelete?: () => void | Promise<void>;
  canDelete?: boolean;
  deleteConfirmHeader?: string;
  deleteConfirmContent?: React.ReactNode;
  /** Extra layer-specific actions, rendered above the standard action buttons. */
  children?: React.ReactNode;
}

const DEFAULT_ACTIONS: LayerPanelAction[] = ["draw", "upload", "export"];

const LayerPanel: React.FC<LayerPanelProps> = ({
  title,
  layerKey,
  testIdPrefix,
  actions = DEFAULT_ACTIONS,
  drawTitle = "Draw",
  drawSubtitle = "Add and edit data on the map",
  onDraw,
  onDelete,
  canDelete = false,
  deleteConfirmHeader,
  deleteConfirmContent,
  children,
}) => {
  const { open, clear } = useStackedNavigationContext();
  const project = useAppSelector((state) => getCurrentProject(state.project));
  const canViewExport = permissionCheck(project?.Permission?.Level, Actions.PROJECT_EXPORT_VIEW);
  const { value: pathPreferencingEnabled } = useFeatureFlag("path_preferencing", false);
  const testPrefix = testIdPrefix || layerKey;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOnDrawButtonClick = () => {
    if (onDraw) {
      onDraw();
    } else {
      open(`${layerKey}-draw`);
    }
  };

  const handleOnUploadPolygon = () => {
    open(`${layerKey}-upload`);
  };

  const handleOnExportLayer = () => {
    open(`${layerKey}-export`);
  };

  const handleOnDeleteButtonClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete();
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const onBack = () => {
    clear();
  };

  return (
    <Box>
      <StackedNavigationHeader title={title} onBackButtonClick={onBack} />
      {children}
      {actions.includes("draw") && (
        <StackedNavigationButton
          data-testid={`draw-${testPrefix}-button`}
          icon={<DriveFileRenameOutline />}
          title={drawTitle}
          subtitle={drawSubtitle}
          onClick={handleOnDrawButtonClick}
        />
      )}
      {actions.includes("upload") && (
        <StackedNavigationButton
          data-testid={`upload-${testPrefix}-button`}
          icon={<CloudUpload />}
          title="Upload"
          subtitle="Use my data"
          onClick={handleOnUploadPolygon}
        />
      )}
      {pathPreferencingEnabled && actions.includes("path-preferences") && (
        <StackedNavigationButton
          data-testid={`path-preferences-${testPrefix}-button`}
          icon={<Edit />}
          title="Path preferences"
          subtitle="Edit path preferences"
          onClick={() => open(`${layerKey}-path-preferences`)}
        />
      )}
      {actions.includes("export") && canViewExport && (
        <StackedNavigationButton
          data-testid={`export-${testPrefix}-button`}
          icon={<FileDownload />}
          title="Export Layer"
          subtitle="Download layer data"
          onClick={handleOnExportLayer}
        />
      )}
      {actions.includes("delete") && onDelete && (
        <>
          <StackedNavigationButton
            data-testid={`delete-${testPrefix}-button`}
            icon={<DeleteOutline />}
            title="Delete"
            subtitle="Remove existing data"
            onClick={handleOnDeleteButtonClick}
            disabled={!canDelete}
          />
          <ConfirmModal
            open={showDeleteConfirm}
            header={deleteConfirmHeader ?? `Delete ${title.toLowerCase()}?`}
            confirmText="Delete"
            cancelText="Cancel"
            color="error"
            content={
              deleteConfirmContent ?? (
                <Typography variant="body2">
                  This will permanently delete all existing {title.toLowerCase()} data. This action cannot be undone.
                </Typography>
              )
            }
            onConfirm={handleConfirmDelete}
            onCancel={() => setShowDeleteConfirm(false)}
            disabled={isDeleting}
          />
        </>
      )}
    </Box>
  );
};

export default LayerPanel;
